import {
  PrimaryKey,
  Timestamp,
  isEnumValue,
  isObject,
  isTimestamp,
} from '@principle-theorem/shared';
import { isString } from 'lodash';

export enum DestinationEntityRecordStatus {
  Failed = 'failed',
  MergeConflict = 'mergeConflict',
  Skipped = 'skipped',
  Migrated = 'migrated',
  MigratedWithErrors = 'migratedWithErrors',
  MissingCustomMapping = 'missingCustomMapping',
}

export const DESTINATION_ENTITY_RECORD_STATUS_MAP: {
  [key in DestinationEntityRecordStatus]: string;
} = {
  [DestinationEntityRecordStatus.Migrated]: 'primary',
  [DestinationEntityRecordStatus.MergeConflict]: 'accent',
  [DestinationEntityRecordStatus.MigratedWithErrors]: 'accent',
  [DestinationEntityRecordStatus.Skipped]: 'primary',
  [DestinationEntityRecordStatus.Failed]: 'warn',
  [DestinationEntityRecordStatus.MissingCustomMapping]: 'accent',
};

export type MergeConflictDestinationEntityRecord = {
  status: DestinationEntityRecordStatus.MergeConflict;
};

export type FailedDestinationEntityRecord = {
  status: DestinationEntityRecordStatus.Failed;
  errorMessage: string;
  failData: object;
};

export type SkippedDestinationEntityRecord = {
  status: DestinationEntityRecordStatus.Skipped;
};

export enum MissingCustomMappingStatus {
  Unresolved = 'unresolved',
  Resolved = 'resolved',
}

export type MissingCustomMappingDestinationEntityRecord<
  FailData extends object = object,
> = {
  status: DestinationEntityRecordStatus.MissingCustomMapping;
  errorMessage: string;
  failData: FailData;
};

export type MigratedWithErrorsDestinationEntityRecord<
  Data = object,
  FailData extends object = object,
> = Omit<MigratedDestinationEntityRecord<Data>, 'status'> & {
  status: DestinationEntityRecordStatus.MigratedWithErrors;
  failData: FailData;
};

export type IMigratedWithErrors<
  T extends object,
  R extends object,
> = IDestinationEntityRecord<T> &
  MigratedWithErrorsDestinationEntityRecord<T, R>;

export type MigratedDestinationEntityRecord<Data = object> = {
  status: DestinationEntityRecordStatus.Migrated;
  data: Data;
  migratedAt: Timestamp;
};

export type IDestinationEntityRecord<
  Data extends object = object,
  FailData extends object = object,
  SourceKey extends object = object,
> = {
  uid: string;
  label: string;
  sourceRef: PrimaryKey<SourceKey>;
  data?: Data;
  modifiedData?: Data;
  jobData?: object;
  diffData?: object;
} & (
  | MergeConflictDestinationEntityRecord
  | FailedDestinationEntityRecord
  | MigratedDestinationEntityRecord<Data>
  | MigratedWithErrorsDestinationEntityRecord<Data, FailData>
  | SkippedDestinationEntityRecord
  | MissingCustomMappingDestinationEntityRecord<FailData>
);

export function isDestinationEntityRecord(
  item: unknown
): item is IDestinationEntityRecord {
  return (
    isObject(item) &&
    isString(item.uid) &&
    isString(item.label) &&
    isEnumValue(DestinationEntityRecordStatus, item.status) &&
    (item.status === DestinationEntityRecordStatus.Skipped ||
      (isString(item.errorMessage) && isObject(item.failData)) ||
      (isObject(item.data) && isTimestamp(item.migratedAt)))
  );
}

export const JOB_DATA_UID = 'jobData';
export const PRINCIPLE_DIFF_DATA_UID = 'principleDiff';
